import {
  PortableText,
  type PortableTextComponents,
  type PortableTextMarkComponentProps,
} from "@portabletext/react";
import { urlForImage } from "../../../sanity/lib/image";
import type { PortableTextBody } from "@/lib/sanity.types";

type PortableTextRendererProps = {
  body: PortableTextBody;
};

/**
 * Derive a stable anchor slug from a heading's text content. Matches the slug
 * format the TOC extractor in `blog-post.tsx` produces so anchor links line up.
 */
function slugify(text: string): string {
  return text
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-")
    .replace(/(^-|-$)+/g, "");
}

function blockToPlainText(children: unknown): string {
  if (!Array.isArray(children)) return "";
  return children
    .map((child) => {
      if (typeof child === "string") return child;
      if (child && typeof child === "object" && "props" in child) {
        const props = (child as { props?: { children?: unknown } }).props;
        return blockToPlainText(props?.children);
      }
      return "";
    })
    .join("");
}

const components: PortableTextComponents = {
  block: {
    normal: ({ children }) => (
      <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-4">{children}</p>
    ),
    h2: ({ children }) => {
      const id = slugify(blockToPlainText(children));
      return (
        <h2
          id={id}
          className="text-2xl font-bold text-gray-900 dark:text-white mb-3 mt-5 scroll-mt-24"
        >
          {children}
        </h2>
      );
    },
    h3: ({ children }) => {
      const id = slugify(blockToPlainText(children));
      return (
        <h3
          id={id}
          className="text-xl font-semibold text-gray-900 dark:text-white mb-3 mt-4 scroll-mt-24"
        >
          {children}
        </h3>
      );
    },
    h4: ({ children }) => (
      <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-2 mt-3 scroll-mt-24">
        {children}
      </h4>
    ),
    blockquote: ({ children }) => (
      <blockquote className="border-l-4 border-blue-500 dark:border-blue-400 pl-4 bg-blue-50/50 dark:bg-blue-900/10 py-2 font-medium not-italic text-gray-700 dark:text-gray-300 mb-4">
        {children}
      </blockquote>
    ),
  },
  list: {
    bullet: ({ children }) => (
      <ul className="list-disc pl-6 mb-4 space-y-1 marker:text-blue-500 dark:marker:text-blue-400">
        {children}
      </ul>
    ),
    number: ({ children }) => (
      <ol className="list-decimal pl-6 mb-4 space-y-1 marker:text-blue-500 dark:marker:text-blue-400">
        {children}
      </ol>
    ),
  },
  listItem: {
    bullet: ({ children }) => <li className="text-gray-700 dark:text-gray-300">{children}</li>,
    number: ({ children }) => <li className="text-gray-700 dark:text-gray-300">{children}</li>,
  },
  marks: {
    strong: ({ children }) => <strong className="font-semibold">{children}</strong>,
    em: ({ children }) => <em className="italic">{children}</em>,
    code: ({ children }) => (
      <code className="bg-gray-100 dark:bg-gray-800 text-gray-800 dark:text-gray-200 px-1.5 py-0.5 rounded text-sm font-mono">
        {children}
      </code>
    ),
    underline: ({ children }) => <span className="underline">{children}</span>,
    link: ({ value, children }: PortableTextMarkComponentProps<{ _type: "link"; href?: string }>) => {
      const href = value?.href ?? "#";
      const isExternal = href.startsWith("http");
      return (
        <a
          href={href}
          className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 underline"
          target={isExternal ? "_blank" : undefined}
          rel={isExternal ? "noopener noreferrer" : undefined}
        >
          {children}
        </a>
      );
    },
  },
  types: {
    image: ({
      value,
    }: {
      value: { alt?: string; caption?: string; asset?: unknown };
    }) => {
      const url = urlForImage(value)?.width(1600).fit("max").auto("format").url();
      if (!url) return null;
      return (
        <figure className="my-8">
          <img
            src={url}
            alt={value.alt ?? ""}
            className="rounded-xl w-full h-auto shadow-lg"
            loading="lazy"
          />
          {value.caption && (
            <figcaption className="text-center text-sm text-gray-500 dark:text-gray-400 mt-3 italic">
              {value.caption}
            </figcaption>
          )}
        </figure>
      );
    },
    codeBlock: ({ value }: { value: { language?: string; code?: string } }) => (
      <pre className="bg-gray-900 dark:bg-[#0d1117] text-gray-100 dark:text-gray-200 p-4 rounded-lg overflow-x-auto mb-4 border border-gray-700 dark:border-gray-800">
        <code className={`language-${value.language ?? "text"} block`}>{value.code ?? ""}</code>
      </pre>
    ),
  },
};

export default function PortableTextRenderer({ body }: PortableTextRendererProps) {
  if (!body || body.length === 0) return null;

  return (
    <div className="prose prose-lg max-w-none">
      <PortableText value={body as never} components={components} />
    </div>
  );
}
